import React, { useState, useEffect } from 'react';
import { Input, Row, Col, Typography, FloatButton, Popconfirm, Button } from 'antd';
import { PlusOutlined, QuestionCircleOutlined, SmileOutlined } from '@ant-design/icons';
import { useFlowStore } from '../store';
import { Topic } from '../types';
import { Topic as TopicType } from '../../api/api';
import AuthenticatedLayout from '../layout/AuthenticatedLayout';
import { useNavigate } from 'react-router-dom';
import { KeyAdmin } from '../constants';

const { Title } = Typography;

const TopicsPage: React.FC = () => {
  const navigate = useNavigate();
  const { topics, deleteTopic, postTopic, getTopics, role } = useFlowStore();

  const [filteredTopics, setFilteredTopics] = useState(topics);
  const [deleteConfirmText, setDeleteConfirmText] = useState<{[title: string]: string}>({});
  const [newTopic, setNewTopic] = useState<TopicType>({ title: ""} as TopicType);

  useEffect(() => {
    // Check if we're being redirected from SSO
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthToken = urlParams.has('token') || urlParams.has('code') || urlParams.has('state');

    if (hasAuthToken) {
      // Clear the URL parameters to avoid issues with refreshing
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    getTopics();
  }, [getTopics]);

  useEffect(() => {
    setFilteredTopics([...topics])
  }, [topics, setFilteredTopics])

  // Handle search input
  const handleSearch = (value: string) => {
    const lowerCaseValue = value.toLowerCase();
    setFilteredTopics(
      topics.filter((topic) =>
        topic.title.toLowerCase().includes(lowerCaseValue)
      )
    );
  };

  const onTopicClick = (topic: Topic) => {
    navigate(`/${topic.title}/map`);
  }

  const onDeleteClick = async (topicToDelete: Topic) => {
      const success = await deleteTopic(topicToDelete.title as string)
      if (success) {
        setFilteredTopics(filteredTopics.filter(topic => topic.title !== topicToDelete.title))
      }
  }

  const onAddClick = async () => {
    if (newTopic.title !== "") {        
      const { success, nid } = await postTopic(newTopic);

      if (success) {
        setFilteredTopics([...filteredTopics, newTopic].sort());
        navigate(`/${newTopic.title}/wiki-page/${nid}`);
      }
      setNewTopic({ title: ""} as TopicType);
    }
  }

  return (
    <AuthenticatedLayout>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Title level={2} style={{ textAlign: 'center' }}>
          Topics
        </Title>
        <Input
          placeholder="Search topics..."
          allowClear
          onChange={(e) => handleSearch(e.target.value)}
        />
        <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
          {filteredTopics.map((topic, index) => (
            <Col xs={24} sm={12} md={8} key={index}>
              <div className='titleBox' onClick={() => onTopicClick(topic)}>
                <span className="topicTitle">{topic.title}</span>
                {role === KeyAdmin && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Popconfirm
                      title={
                        <div>
                          <div>Delete the topic</div>
                          <div style={{ marginBottom: 8 }}>
                            Are you sure you want to delete all nodes and edges for{" "}
                            <b>{topic.title}</b>?
                          </div>
                          <div>
                            <div>Type the topic name to confirm:</div>
                            <Input
                              placeholder={topic.title}
                              value={deleteConfirmText[topic.title] || ""}
                              onChange={(e) =>
                                setDeleteConfirmText({
                                  ...deleteConfirmText,
                                  [topic.title]: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      }
                      onConfirm={() => {
                        if ((deleteConfirmText[topic.title] || "") === topic.title) {
                          onDeleteClick(topic);
                          // clear out after success
                          setDeleteConfirmText((prev) => {
                            const copy = { ...prev };
                            delete copy[topic.title];
                            return copy;
                          });
                        } else {
                          alert("Topic name does not match. Deletion canceled.");
                        }
                      }}
                      okText="Delete"
                      cancelText="Cancel"
                      icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                    >
                      <button>Delete</button>
                    </Popconfirm>
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
        {role === KeyAdmin && (
          <Popconfirm
            title={
              <div>
                <div>Enter new topic</div>
                <Input
                  placeholder="New topic"
                  value={newTopic.title}
                  onChange={(e) => setNewTopic({title: e.target.value})}
                />
              </div>
            }
            onConfirm={onAddClick}
            okText="Add"
            cancelText="Cancel"
            icon={<SmileOutlined style={{ color: 'green' }} />}
          >
            <FloatButton icon={<PlusOutlined />} type="primary" style={{ insetInlineEnd: 94 }} />
          </Popconfirm>
        )}
      </div>
    </AuthenticatedLayout>
  );
};

export default TopicsPage;
